<script lang="ts">
import {defineComponent} from 'vue'
import '@banno/jha-wc/src/icons/jha-icon-edit.js'
import type {bltModalDialog} from '@/common/components/appch.components.module'
import {BltSignatureField} from '@/common/constant/bltSignatureFieldConstant'
import bltSignatureFormDialog from '@/common/components/bltSignatureFormDialog/bltSignatureFormDialog.vue'
import LanguageFactory from '@/common/services/Language/LanguageFactory'
import {$inj} from '@/common/decorators/depinject'
import {getRgbaFromAnyValidCssColor} from '@/common/services/utils/canvasUtil'
import BltForm from '@/open/components/bltForm1.vue'
export default defineComponent({
  name: 'bltSignatureField',
  emits: ['signatureChanged'],
  components: {BltForm, bltSignatureFormDialog},
  props: {
    padding: {
      type: String,
      required: false,
      default: "48px 16px"
    },
    imageHeight: {
      type: String,
      required: false,
      default: "122px"
    },
    signatureLabel: {
      type: String,
      default: ''
    },
    signatureImage: {
      type: String,
      default: ''
    },
    dialogBorder: {
      type: Boolean,
      default: true
    },
    dialogTitle: {
      type: String,
      default: ''
    },
    hideLabel: {
      type: Boolean,
      default: () => false
    },
    disableClick: {
      type: Boolean,
      default: false
    },
    require: {
      type: Boolean,
      default: false
    },
    warning: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      BltSignatureField,
      base64Image: '' as string,
      showDialog: false as boolean,
      previewBase64: '' as string,
      blurred: false as boolean,
      signatureInput: '' as string
    }
  },
  setup() {
    const languageFactory: LanguageFactory = $inj(LanguageFactory)
    return {languageFactory}
  },
  mounted() {
    this.signatureInput = this.getImage
    this.renderPreview()
  },
  computed: {
    isEditMode() {
      return this.getImage !== ''
    },
    getImage() {
      return this.base64Image ? this.base64Image : this.signatureImage
    },
    isRequired() {
      return this.require
    },
    signatureLabelRequired() {
      return this.isRequired ? this.signatureLabel + ' *' : this.signatureLabel
    },
    isValid() {
      return (
        (this.isRequired && this.signatureInput.length > 0 && this.blurred) ||
        !this.isRequired ||
        (this.isRequired && !this.blurred)
      )
    },
    isValidButtonClass() {
      return this.isValid ? 'reset main' : 'reset err'
    },
    isValidWarningClass() {
      return this.isValid ? '' : 'err'
    },
    isValidLabelClass() {
      return this.isValid ? '' : 'warn err'
    },
    isValidLinkClass() {
      return this.isValid ? 'sign-link' : 'sign-link-err'
    }
  },
  watch: {
    getImage: function () {
      this.renderPreview()
    },
    signatureImage: function () {
      const input = this.$refs.hiddenSignatureField as HTMLInputElement
      const testD = this.$refs.testDiv as HTMLDivElement
      if (this.isRequired && this.signatureImage.length == 0) {
        input.setCustomValidity(this.warning)
      } else {
        input.setCustomValidity('')
      }
      this.signatureInput = this.getImage
    }
  },
  methods: {
    renderPreview() {
      const TRANSPARENT_ALPHA = 0
      const RED_OFFSET = 0
      const GREEN_OFFSET = 1
      const BLUE_OFFSET = 2
      const ALPHA_OFFSET = 3
      const bodyComputedStyles = getComputedStyle(window.document.body)
      const jhaTextBaseColor = bodyComputedStyles.getPropertyValue('--jha-text-base')
      const signatureStokeOverride = bodyComputedStyles.getPropertyValue('--jha-signature-fill-color')
      const lineColor = signatureStokeOverride || jhaTextBaseColor
      const canvas = document.createElement('canvas')
      canvas.height = 300
      canvas.width = 700
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.error('Unable to load context when trying to render signature canvas preview.')
        return
      }
      const image = new Image()
      image.src = this.getImage
      image.onload = () => {
        ctx.drawImage(image, 0, 0)
        const {width, height} = canvas
        const imageData = ctx.getImageData(0, 0, width, height)
        const pixels = imageData.data
        const {r, g, b, a} = getRgbaFromAnyValidCssColor(lineColor)
        for (let i = 0; i < pixels.length; i += 4) {
          const pixelAlpha = pixels[i + ALPHA_OFFSET]
          if (pixelAlpha !== TRANSPARENT_ALPHA) {
            pixels[i + RED_OFFSET] = r
            pixels[i + BLUE_OFFSET] = g
            pixels[i + GREEN_OFFSET] = b
            pixels[i + ALPHA_OFFSET] = a
          }
        }
        ctx.putImageData(imageData, 0, 0)
        this.previewBase64 = canvas.toDataURL()
      }
    },
    buttonClick() {
      if (!this.disableClick) {
        ;(this.$refs.modalDialog as typeof bltModalDialog).open()
      }
      this.blurred = true
    },
    save(event: any) {
      this.base64Image = event.detail
      this.$emit('signatureChanged', event)
      ;(this.$refs.modalDialog as typeof bltModalDialog).close()
    },
    cancel() {
      ;(this.$refs.modalDialog as typeof bltModalDialog).close()
    },
    doBlur() {
      this.blurred = true
    }
  }
})
</script>

<template>
  <div class="blt-signature-field-wrapper">
    <label
      v-if="!hideLabel"
      :class="isValidWarningClass">
      {{ signatureLabelRequired }}</label
    >
    <input
      type="text"
      ref="hiddenSignatureField"
      style="display: none"
      :required="isRequired"
      v-model="signatureInput"
      @blur="doBlur" />
    <div
      class="signature"
      :style="{'height': imageHeight}"
      v-if="isEditMode"
      id="testDiv">
      <img
        :src="previewBase64"
        alt="signature" />
      <button
        :style="{padding}"
        class="edit reset"
        type="button"
        @click="buttonClick"
        :disabled="disableClick">
        {{ BltSignatureField.EDIT }}
      </button>
    </div>
    <div v-else>
      <button
        @click="buttonClick"
        type="button"
        :style="{padding}"
        :class="isValidButtonClass"
        :disabled="disableClick">
        <jha-icon-edit></jha-icon-edit>
        <span :class="isValidLinkClass"> {{ BltSignatureField.ADD_YOUR_SIGNATURE }}</span>
      </button>
    </div>
    <div
      v-if="!isValid"
      :class="isValidLabelClass">
      {{ warning }}
    </div>
    <blt-modal-dialog
      type="small-dialog"
      ref="modalDialog"
      :title="dialogTitle || BltSignatureField.SIGNATURE"
      :showFooter="false"
      :dialogBorder="false">
      <template v-slot:dialog-content>
        <bltSignatureFormDialog
          :dialogTitle="dialogTitle"
          @signatureChanged="save($event)">
        </bltSignatureFormDialog>
      </template>
    </blt-modal-dialog>
  </div>
</template>

<style scoped>
.blt-signature-field-wrapper {
  display: block;
  margin-top: var(--jh-space-400);
  margin-bottom: var(--jh-space-200);
  font-family: 'roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  font-weight: 400;
  color: var(--body-text-primary-color);
  font-size: 14px;
}
.err {
  color: var(--jha-color-danger, #db2012);
}
.warn {
  font-size: 12px;
  margin-top: var(--jh-space-200);
}
button.reset {
  box-sizing: border-box;
  height: auto;
  color: var(--jh-color-content-brand-enabled);
  touch-action: manipulation;
  background: none;
  border: none;
  cursor: pointer;
  display: block;
  font-family: 'roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  font-size: 14px;
  margin: 0;
  padding: 0;
  text-align: left;
  width: 100%;
  -webkit-appearance: none;
}

button.edit {
  position: absolute;
  top: 4px;
  right: 0;
  padding: 8px 16px;
  z-index: 1;
  width: auto;
  color: var(--link-button-text-color);
}

button.main {
  border: 1px solid var(--jha-form-floating-group-outline-border-color, var(--jha-border-color, #8c989f));
  border-radius: 4px;
  margin: 0px 0 0px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}
button.err {
  border: 1px solid var(--jha-color-danger, var(--jha-border-color, #db2012));
  border-radius: 4px;
  margin: 0px 0 0px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}
button.err:hover {
  border: 1px solid #000000;
  border-radius: 4px;
  margin: 0px 0 0px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.signature {
  border: 1px solid var(--jha-form-floating-group-outline-border-color, var(--jha-border-color, #8c989f));
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

img {
  max-height: 100%;
  max-width: 100%;
  margin: 0 auto;
  display: block;
}

jha-icon-edit {
  margin-right: 8px;
  fill: var(--link-button-text-color);
}
.sign-link {
  color: var(--link-button-text-color);
}
.sign-link-err {
  color: var(--jha-color-danger, #db2012);
}
</style>
